import { api } from '../lib/api.ts';
import type { User } from '../lib/auth.ts';
import { useStore } from '../lib/store.ts';

/** The browser's IANA time zone ("Europe/Kyiv"), or null if unknown. */
export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Send the browser's time zone when the server has another one
 * (docs/USER_TIMEZONE_PLAN.md); the next /auth/me then matches, so it is
 * sent once. Best effort. */
export async function syncTimezone(user: User): Promise<void> {
  const zone = browserTimezone();
  if (!zone || user.timezone === zone) return;
  try {
    await api.setTimezone(zone);
  } catch {
    // Unknown zone or offline: the server keeps its fallback.
  }
}

/** Fetch the user's plan and usage from /auth/me into the store, then keep
 * the server's time zone in step with the browser. Best effort. */
export async function refreshPlan(): Promise<void> {
  try {
    const user = await api.me();
    useStore.getState().setUser(user);
    await syncTimezone(user);
  } catch {
    // Offline or signed out: keep what we have.
  }
}

/** Whether Strava is on for this user (owner-only; hidden for everyone else). */
export function useStravaEnabled(): boolean {
  return useStore((s) => s.user?.features?.strava === true);
}

/** Whether Garmin Connect is on for this user (``features.garmin``). */
export function useGarminEnabled(): boolean {
  return useStore((s) => s.user?.features?.garmin === true);
}

/** Whether intervals.icu is on for this user (``features.intervals``). */
export function useIntervalsEnabled(): boolean {
  return useStore((s) => s.user?.features?.intervals === true);
}

/** Whether other activities (runs, swims, gym) are on (``features.activities``). */
export function useActivitiesEnabled(): boolean {
  return useStore((s) => s.user?.features?.activities === true);
}

/** The signed-in user's plan; ``isPro`` is false until the plan is known. */
export function usePlan() {
  const user = useStore((s) => s.user);
  return {
    isPro: user?.plan === 'pro',
    isFree: user?.plan === 'free',
    freeChatLeft: user?.free_chat_left ?? null,
    freeChatMonthly: user?.free_chat_monthly ?? null,
    proUntil: user?.pro_until ?? null,
    trial: user?.trial ?? false,
  };
}
